import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Button,
  FormLabel,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";
import "assets/CSS/Switch.css";

const InventoryManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // Top cards data
  const topCardsData = [
    {
      id: 1,
      value: "156",
      label: "Total Materials",
      icon: "lucide:boxes",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
    },
    {
      id: 2,
      value: "24",
      label: "Total Assets",
      icon: "solar:money-bag-broken",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 3,
      value: "142",
      label: "Synced Items",
      icon: "material-symbols:sync-rounded",
      iconColor: "#DD992F",
      bgColor: "#FFF3E0",
    },
    {
      id: 4,
      value: "8",
      label: "Sync Errors",
      icon: "solar:danger-triangle-linear",
      iconColor: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  // Materials & Equipment Data with toggle states
  const [materialsData, setMaterialsData] = useState([
    {
      id: 1,
      sync: true,
      status: "Synced",
      cleriAssetName: "Bulldozer CAT D6T",
      qbAssetName: "Bulldozer CAT D6T",
      cleriSerialVIN: "CAT-D6T-2019-001",
      qbSKU: "BD-CAT-D6T-001",
      category: "Materials",
    },
    {
      id: 2,
      sync: true,
      status: "Synced",
      cleriAssetName: "Dump Truck - Ford F750",
      qbAssetName: "Dump Truck Ford F750",
      cleriSerialVIN: "1FDNF7HT8KDA12345",
      qbSKU: "DT-FORD-F750-002",
      category: "Materials",
    },
    {
      id: 3,
      sync: false,
      status: "Pending",
      cleriAssetName: "Concrete Mixer - Portable",
      qbAssetName: "Concrete Mixer Portable",
      cleriSerialVIN: "MIX-PORT-2020-003",
      qbSKU: "MIX-PORT-003",
      category: "Equipment",
    },
    {
      id: 4,
      sync: true,
      status: "Pending",
      cleriAssetName: "Concrete Mixer - Portable",
      qbAssetName: "Concrete Mixer Portable",
      cleriSerialVIN: "MIX-PORT-2020-003",
      qbSKU: "MIX-PORT-003",
      category: "Equipment",
    },
    {
      id: 5,
      sync: false,
      status: "Synced",
      cleriAssetName: "Concrete Mixer - Portable",
      qbAssetName: "Concrete Mixer Portable",
      cleriSerialVIN: "MIX-PORT-2020-003",
      qbSKU: "MIX-PORT-003",
      category: "Equipment",
    },
  ]);

  // Assets Data with toggle states
  const [assetsData, setAssetsData] = useState([
    {
      id: 1,
      sync: true,
      status: "Synced",
      cleriAssetName: "Excavator XL2000",
      qbAssetName: "Excavator XL2000",
      cleriSerialVIN: "EXC-XL2000-2021-001",
      qbSKU: "EXC-XL2000-001",
      category: "Heavy Equipment",
    },
    {
      id: 2,
      sync: false,
      status: "Error",
      cleriAssetName: "Safety Cones - Orange",
      qbAssetName: "Safety Cones Orange",
      cleriSerialVIN: "SAF-CON-ORG-001",
      qbSKU: "SAF-CON-ORG-001",
      category: "Safety Equipment",
    },
    {
      id: 3,
      sync: false,
      status: "Error",
      cleriAssetName: "Safety Cones - Orange",
      qbAssetName: "Safety Cones Orange",
      cleriSerialVIN: "SAF-CON-ORG-001",
      qbSKU: "SAF-CON-ORG-001",
      category: "Safety Equipment",
    },
  ]);

  // Handler to update sync toggle for materials
  const handleMaterialsSyncChange = (id, checked) => {
    setMaterialsData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, sync: checked } : item
      )
    );
  };

  // Handler to update sync toggle for assets
  const handleAssetsSyncChange = (id, checked) => {
    setAssetsData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, sync: checked } : item
      )
    );
  };

  // DataGrid columns for both tabs
  const columns = [
    {
      field: "sync",
      headerName: "Sync",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <label className="switch">
            <input
              type="checkbox"
              checked={params.value}
              onChange={(e) => {
                if (activeTab === 0) {
                  handleMaterialsSyncChange(params.row.id, e.target.checked);
                } else {
                  handleAssetsSyncChange(params.row.id, e.target.checked);
                }
              }}
            />
            <span className="slider round"></span>
          </label>
          <FormLabel sx={{ ml: 1, fontSize: "0.75rem" }}>
            {params.value ? "Yes" : "No"}
          </FormLabel>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Chip
            label={params.value}
            size="small"
            sx={{
              backgroundColor:
                params.value === "Synced"
                  ? "#E8F5E9"
                  : params.value === "Pending"
                  ? "#FFF3E0"
                  : "#FFEBEE",
              color:
                params.value === "Synced"
                  ? "#23A26D"
                  : params.value === "Pending"
                  ? "#DD992F"
                  : "#D80027",

              height: 24,
            }}
          />
        </Box>
      ),
    },
    {
      field: "cleriAssetName",
      headerName: "Cleri Asset Name",
      width: 200,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "qbAssetName",
      headerName: "QB Asset Name",
      width: 180,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "cleriSerialVIN",
      headerName: "Cleri Serial / VIN",
      width: 160,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "qbSKU",
      headerName: "QB SKU",
      width: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "category",
      headerName: "Category",
      width: 130,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          {params.value}
        </Typography>
      ),
    },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleNavigation = (to) => {
    navigate(to);
  };

  const handlePageBack = () => {
    handleNavigation("/quickbooks");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Quickbooks/Inventory"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        <Card sx={{ borderRadius: 1, overflow: "hidden" }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
              <Button
                variant="contained"
                color="main"
                size="medium"
                startIcon={
                  <Icon icon="heroicons:arrow-path" width={20} height={20} />
                }
              >
                Sync Now
              </Button>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {topCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 160,
                      }}
                    >
                      {/* Icon on the left */}
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Materials & Equipment Section with Tabs */}
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h4">Inventory Management</Typography>
                    <Typography variant="body1">
                      Manage materials and assets sync with QuickBooks
                      Products/Services
                    </Typography>
                  </Box>

                  <CustomTextField
                    placeholder="Search"
                    size="small"
                    value={searchTerm}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon icon="tabler:search" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Tabs */}
                <Box sx={{ px: 3, mb: 5 }}>
                  <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Materials & Equipment" />
                    <Tab label="Assets" />
                  </Tabs>
                </Box>

                {/* DataGrid Content */}
                <Box
                  sx={{
                    height: 400,
                    width: "100%",
                    mt: 5,
                    mx: "auto",
                  }}
                >
                  <DataGridPro
                    rows={activeTab === 0 ? materialsData : assetsData}
                    columns={columns}
                    pagination
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    disableSelectionOnClick
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    sx={{
                      "& .MuiDataGrid-columnHeaderTitle": {
                        textTransform: "none",
                        fontWeight: 600,
                      },
                      "& .MuiDataGrid-cell": {
                        textTransform: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </Box>
    </UserLayout>
  );
};

export default InventoryManagement;
